import React, { useEffect, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import ContainerDashboard from '../../components/layouts/ContainerDashboard'
import Loader from '../Loader'
import { axiosInstance } from '../Config'
import moment from 'moment'

const actionColors = {
    Created: { bg: '#e8f8f0', color: '#1a9e5c', dot: '#1a9e5c' },
    Updated: { bg: '#fff7e6', color: '#d48806', dot: '#d48806' },
    Deleted: { bg: '#fff0f0', color: '#e03131', dot: '#e03131' },
    Uploaded: { bg: '#e8f0fe', color: '#4f6ef7', dot: '#4f6ef7' },
    Viewed: { bg: '#f0f0ff', color: '#7c3aed', dot: '#7c3aed' },
}
const moduleColors = {
    'Staff': { bg: '#eef2ff', color: '#4f6ef7' },
    'Videos': { bg: '#f0fef9', color: '#1a9e5c' },
    'AI Generated': { bg: '#fdf4ff', color: '#9333ea' },
    'Roles & Permissions': { bg: '#fff7e6', color: '#d48806' },
    'AI Chats': { bg: '#e8f4fd', color: '#019CF1' },
}

const formatKey = (k) => {
    if (!k) return ''
    return k.replace(/[_-]/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ').trim().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
}

const isDateString = (v) => {
    if (typeof v !== 'string') return false
    if (!isNaN(Number(v)) && v.length < 9) return false
    const d = Date.parse(v)
    if (isNaN(d)) return false
    return /^\d{4}-\d{2}-\d{2}T|\d{4}-\d{2}-\d{2} /.test(v) || moment(v, moment.ISO_8601, true).isValid()
}

const isImageUrl = (v) => typeof v === 'string' && (v.startsWith('http://') || v.startsWith('https://') || v.startsWith('data:image')) && (/\.(jpeg|jpg|gif|png|webp|svg)$/i.test(v.split('?')[0]) || v.startsWith('data:image'))

const isIndexedCharObject = (obj) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return false
    const keys = Object.keys(obj)
    if (!keys.length) return false
    if (!keys.every(k => /^\d+$/.test(k))) return false
    const nums = keys.map(Number).sort((a,b)=>a-b)
    for (let i=0;i<nums.length;i++) if (nums[i]!==i) return false
    return Object.values(obj).every(v => typeof v === 'string' && v.length===1)
}

const indexedCharToString = (obj) => Object.keys(obj).sort((a,b)=>Number(a)-Number(b)).map(k=>obj[k]).join('')

const sanitizeValue = (value) => {
    if (value === null || value === undefined) return value
    if (Array.isArray(value)) return value.map(sanitizeValue)
    if (typeof value === 'object') {
        if (isIndexedCharObject(value)) {
            const s = indexedCharToString(value)
            return s
        }
        const out = {}
        Object.entries(value).forEach(([k,v])=> out[k]=sanitizeValue(v))
        return out
    }
    return value
}

const DetailValue = ({ value }) => {
    const v = sanitizeValue(value)
    if (v === null || v === undefined || v === '') return <span style={{ color: '#bbb' }}>—</span>
    if (typeof v === 'boolean') return <span>{v ? 'Yes' : 'No'}</span>
    if (Array.isArray(v)) {
        if (!v.length) return <span style={{ color: '#bbb' }}>—</span>
        if (v.every(x => typeof x !== 'object')) return <span>{v.join(', ')}</span>
        return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, background: '#f8f9fa', padding: 8, borderRadius: 6 }}>{JSON.stringify(v, null, 2)}</pre>
    }
    if (typeof v === 'object') return <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12, background: '#f8f9fa', padding: 8, borderRadius: 6 }}>{JSON.stringify(v, null, 2)}</pre>
    if (isImageUrl(v)) return <a href={v} target="_blank" rel="noreferrer"><img src={v} alt="preview" style={{ maxWidth: 120, maxHeight: 80, borderRadius: 6, border: '1px solid #eee', objectFit: 'cover' }} /></a>
    if (isDateString(v)) return <span>{moment(v).format('DD MMM YYYY, hh:mm A')}</span>
    return <span style={{ wordBreak: 'break-word' }}>{String(v)}</span>
}

const parseDetails = (raw) => {
    if (raw === null || raw === undefined) return null
    if (typeof raw === 'string') {
        const t = raw.trim()
        if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
            try { return JSON.parse(t) } catch { return raw }
        }
        return raw
    }
    return raw
}

const normalizeDetails = (detailsRaw, action) => {
    let details = parseDetails(detailsRaw)
    details = sanitizeValue(details)
    if (!details) return null
    if (typeof details === 'string') return { mode: action?.toLowerCase(), data: details }
    if (Array.isArray(details)) return { mode: action?.toLowerCase(), data: details }
    if (typeof details !== 'object') return { mode: action?.toLowerCase(), data: details }
    const keys = Object.keys(details)
    if (!keys.length) return null
    const lowerMap = {}
    keys.forEach(k => lowerMap[k.toLowerCase()] = k)
    const oldCandidates = ['olddata', 'old', 'before', 'previous', 'oldvalues', 'original', 'old_data']
    const newCandidates = ['newdata', 'new', 'after', 'current', 'newvalues', 'updated', 'new_data', 'changes', 'updateddata', 'newvalue']
    const deletedCandidates = ['deleteddata', 'deleted', 'removeddata', 'removed', 'data_deleted']
    const createdCandidates = ['createddata', 'created', 'newrecord', 'inserteddata']
    const oldFound = oldCandidates.find(c => lowerMap[c])
    const newFound = newCandidates.find(c => lowerMap[c])
    const delFound = deletedCandidates.find(c => lowerMap[c])
    const createdFound = createdCandidates.find(c => lowerMap[c])
    if (oldFound && newFound) {
        return { mode: 'updated', oldData: details[lowerMap[oldFound]], newData: details[lowerMap[newFound]] }
    }
    if (oldFound && !newFound) {
        const oldVal = details[lowerMap[oldFound]]
        const otherKeys = keys.filter(k => k !== lowerMap[oldFound])
        if (otherKeys.length === 1 && typeof details[otherKeys[0]] === 'object' && details[otherKeys[0]] !== null) {
            return { mode: 'updated', oldData: oldVal, newData: details[otherKeys[0]] }
        }
        if (otherKeys.length === 0) return { mode: 'updated', oldData: oldVal, newData: null }
        return { mode: 'updated', oldData: oldVal, newData: null }
    }
    if (!oldFound && newFound) {
        const newVal = details[lowerMap[newFound]]
        const otherKeys = keys.filter(k => k !== lowerMap[newFound])
        if (otherKeys.length === 0) return { mode: 'updated', oldData: null, newData: newVal }
        return { mode: 'updated', oldData: null, newData: newVal }
    }
    if (delFound) {
        const v = details[lowerMap[delFound]]
        if (v && typeof v === 'object') return { mode: 'deleted', data: v }
        if (typeof v === 'string') return { mode: 'deleted', data: v }
    }
    if (createdFound) {
        const v = details[lowerMap[createdFound]]
        if (v && typeof v === 'object') return { mode: 'created', data: v }
        if (typeof v === 'string') return { mode: 'created', data: v }
    }
    if (keys.length === 1 && lowerMap['data'] && typeof details[lowerMap['data']] === 'object' && details[lowerMap['data']] !== null) {
        const inner = details[lowerMap['data']]
        return { mode: (action || '').toLowerCase(), data: inner }
    }
    return { mode: (action || '').toLowerCase(), data: details }
}

const DetailGrid = ({ data }) => {
    if (!data || typeof data !== 'object') return <div className="p-3 text-muted">No data</div>
    const entries = Object.entries(data)
    if (!entries.length) return <div className="p-3 text-muted">No fields</div>
    return (
        <div className="table-responsive">
            <table className="table table-bordered mb-0" style={{ fontSize: 13 }}>
                <tbody>
                    {entries.map(([k, v]) => (
                        <tr key={k}>
                            <th style={{ width: 220, background: '#f8f9ff', color: '#333', verticalAlign: 'top', fontWeight: 600, textTransform: 'capitalize' }}>{formatKey(k)}</th>
                            <td style={{ verticalAlign: 'top', wordBreak: 'break-word' }}><DetailValue value={v} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

const UpdatedDiff = ({ oldData, newData }) => {
    const oldObj = oldData && typeof oldData === 'object' && !Array.isArray(oldData) ? oldData : {}
    const newObj = newData && typeof newData === 'object' && !Array.isArray(newData) ? newData : {}
    const allKeys = Array.from(new Set([...Object.keys(oldObj), ...Object.keys(newObj)]))
    if (!allKeys.length) return <div className="p-3 text-muted">No changes data</div>
    return (
        <div className="table-responsive">
            <table className="table table-bordered mb-0" style={{ fontSize: 13 }}>
                <thead style={{ background: '#f8f9ff' }}>
                    <tr>
                        <th style={{ width: 200 }}>Field</th>
                        <th style={{ background: '#fff7e6' }}>Old Value</th>
                        <th style={{ background: '#e8f8f0' }}>New Value</th>
                    </tr>
                </thead>
                <tbody>
                    {allKeys.map(k => {
                        const o = oldObj[k]
                        const n = newObj[k]
                        const changed = JSON.stringify(o) !== JSON.stringify(n)
                        return (
                            <tr key={k} style={{ background: changed ? '#fffef5' : 'transparent' }}>
                                <th style={{ background: changed ? '#fff9db' : '#f8f9ff', verticalAlign: 'top', fontWeight: 600 }}>{formatKey(k)} {changed && <span style={{ marginLeft: 6, fontSize: 10, background: '#ffc658', color: '#7a4a00', padding: '1px 6px', borderRadius: 10 }}>changed</span>}</th>
                                <td style={{ verticalAlign: 'top', background: changed ? '#fff3e0' : '#fff', borderLeft: changed ? '3px solid #ffb74d' : undefined }}><DetailValue value={o} /></td>
                                <td style={{ verticalAlign: 'top', background: changed ? '#e8f5e9' : '#fff', borderLeft: changed ? '3px solid #66bb6a' : undefined }}><DetailValue value={n} /></td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

const AuditLogDetails = () => {
    const { id } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const [log, setLog] = useState(location.state?.log || null)
    const [loading, setLoading] = useState(!location.state?.log)
    const [error, setError] = useState(null)

    useEffect(() => {
        window.scrollTo(0, 0)
        document.title = 'Audit Log Details | Mindora'
    }, [])

    useEffect(() => {
        if (log) return
        if (!id) return
        setLoading(true)
        const tryEndpoints = [`audit-logs/${id}`, `audit-log/${id}`, `audit-logs/details/${id}`, `audit-logs/${id}/details`]
        let idx = 0
        const tryNext = () => {
            if (idx >= tryEndpoints.length) {
                setError('Failed to load audit log details')
                setLoading(false)
                return
            }
            axiosInstance.get(tryEndpoints[idx])
                .then(res => {
                    const d = res.data?.data || res.data?.log || res.data
                    if (d && (d.id || d._id || d.action)) {
                        setLog(d)
                        setError(null)
                        setLoading(false)
                    } else if (Array.isArray(d) && d.length) {
                        setLog(d[0])
                        setLoading(false)
                    } else {
                        idx += 1
                        tryNext()
                    }
                })
                .catch(() => {
                    idx += 1
                    tryNext()
                })
        }
        tryNext()
    }, [id, log])

    const detailsRaw = log?.details ?? log?.detail ?? log?.meta ?? log?.data ?? null
    const normalized = detailsRaw ? normalizeDetails(detailsRaw, log?.action) : null
    const actionMeta = actionColors[log?.action] || { bg: '#f5f5f5', color: '#555', dot: '#999' }
    const moduleMeta = moduleColors[log?.module] || { bg: '#f5f5f5', color: '#555' }
    const performer = log?.performedBy ?? log?.user ?? {}
    const displayName = performer.name || performer.username || performer.email || 'Unknown'

    const renderDetailsSection = () => {
        if (!detailsRaw) return <div className="alert alert-info mb-0">No additional details available for this log.</div>
        if (typeof normalized?.data === 'string') {
            return <div className="p-3"><DetailValue value={normalized.data} /></div>
        }
        if (normalized?.mode === 'updated') {
            if (normalized.oldData || normalized.newData) {
                return <UpdatedDiff oldData={normalized.oldData} newData={normalized.newData} />
            }
            if (normalized.data) return <DetailGrid data={normalized.data} />
            return <div className="alert alert-info mb-0">No change data available.</div>
        }
        if (normalized?.mode === 'created' || normalized?.mode === 'deleted') {
            const d = normalized.data || detailsRaw
            if (typeof d === 'object') return <DetailGrid data={d} />
            return <div className="p-3"><DetailValue value={d} /></div>
        }
        if (normalized?.data && typeof normalized.data === 'object') return <DetailGrid data={normalized.data} />
        if (typeof detailsRaw === 'object') return <DetailGrid data={detailsRaw} />
        return <div className="p-3"><DetailValue value={detailsRaw} /></div>
    }

    const getDetailsTitle = () => {
        if (!normalized) return 'Details'
        if (normalized.mode === 'updated') return 'Changes — Old vs New'
        if (normalized.mode === 'created') return 'Created Data'
        if (normalized.mode === 'deleted') return 'Deleted Data'
        return 'Details'
    }

    const getDetailsIcon = () => {
        if (normalized?.mode === 'updated') return 'bi-arrow-left-right'
        if (normalized?.mode === 'created') return 'bi-plus-circle'
        if (normalized?.mode === 'deleted') return 'bi-trash'
        return 'bi-info-circle'
    }

    if (loading) return <><Loader /><ContainerDashboard title="Audit Log Details" description="Loading..."><div style={{ minHeight: '60vh' }} /></ContainerDashboard></>
    if (error && !log) return (
        <ContainerDashboard title="Audit Log Details" description="Details">
            <div className="p-4 py-5 bg-gradientt rounded-4" style={{ minHeight: '60vh' }}>
                <div className="alert alert-danger">{error}</div>
                <button onClick={() => navigate(-1)} className="button" style={{ background: '#1a1a2e', color: '#fff', borderRadius: 12 }}><i className="icon-arrow-left me-2"></i>Back</button>
            </div>
        </ContainerDashboard>
    )
    if (!log) return (
        <ContainerDashboard title="Audit Log Details" description="Not found">
            <div className="p-4 py-5 bg-gradientt rounded-4" style={{ minHeight: '60vh' }}>
                <div className="alert alert-warning">Audit log not found.</div>
                <button onClick={() => navigate(-1)} className="button" style={{ background: '#1a1a2e', color: '#fff', borderRadius: 12 }}><i className="icon-arrow-left me-2"></i>Back</button>
            </div>
        </ContainerDashboard>
    )

    return (
        <>
            {loading && <Loader />}
            <ContainerDashboard title="Audit Log Details" description={log.description || 'View detailed information'}>
                <section className="ps-dashboards" id="homepages">
                    <div className="row">
                        <div className="col-12">
                            <div className="p-4 py-4 bg-gradientt rounded-4" style={{ minHeight: '75vh' }}>
                                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                                    <button onClick={() => navigate(-1)} className="button" style={{ background: '#1a1a2e', color: '#fff', borderRadius: 12 }}>
                                        <i className="icon-arrow-left me-2"></i>Back
                                    </button>
                                    <span className="d-inline-flex align-items-center gap-1 px-3 py-2 rounded-pill fw-semibold" style={{ background: actionMeta.bg, color: actionMeta.color, fontSize: 13 }}>
                                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: actionMeta.dot, display: 'inline-block' }}></span>
                                        {log.action}
                                    </span>
                                </div>

                                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 12, overflow: 'hidden' }}>
                                    <div className="card-body p-4">
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <p className="mb-1" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#999' }}>Performed By</p>
                                                <div className="d-flex align-items-center gap-3">
                                                    {performer.profilePicture ? <img src={performer.profilePicture} alt={displayName} className="rounded-circle" style={{ width: 42, height: 42, objectFit: 'cover' }} /> : <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 42, height: 42, background: '#8A34F8', color: '#fff', fontWeight: 700 }}>{displayName.slice(0, 2).toUpperCase()}</div>}
                                                    <div>
                                                        <p className="mb-0 fw-semibold" style={{ color: '#1a1a2e' }}>{displayName}</p>
                                                        <p className="mb-0" style={{ fontSize: 12, color: '#888' }}>{performer.role || performer.email || ''}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-3">
                                                <p className="mb-1" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#999' }}>Module</p>
                                                <span className="px-2 py-1 rounded-3 fw-semibold" style={{ background: moduleMeta.bg, color: moduleMeta.color, fontSize: 12 }}>{log.module || '—'}</span>
                                                {log.subModule && <div className="mt-2"><span className="px-2 py-1 rounded-3" style={{ background: '#f5f5f5', color: '#666', fontSize: 12 }}>{log.subModule}</span></div>}
                                            </div>
                                            <div className="col-md-3">
                                                <p className="mb-1" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#999' }}>Date & Time</p>
                                                <p className="mb-0 fw-semibold" style={{ color: '#1a1a2e' }}>{moment(log.createdAt).format('DD MMM YYYY')}</p>
                                                <p className="mb-0" style={{ fontSize: 12, color: '#888' }}>{moment(log.createdAt).format('hh:mm A')}</p>
                                            </div>
                                            <div className="col-12 mt-3">
                                                <p className="mb-1" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#999' }}>Description</p>
                                                <p className="mb-0" style={{ color: '#444', lineHeight: 1.6 }}>{log.description || '—'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card border-0 shadow-sm" style={{ borderRadius: 12, overflow: 'hidden' }}>
                                    <div className="card-header bg-white d-flex align-items-center gap-2 py-3" style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <i className={`bi ${getDetailsIcon()}`} style={{ fontSize: 18, color: '#4f46e5' }}></i>
                                        <h6 className="mb-0 fw-bold" style={{ color: '#1a1a2e' }}>{getDetailsTitle()}</h6>
                                        {normalized?.mode === 'updated' && <span className="ms-auto badge" style={{ background: '#fff7e6', color: '#d48806', border: '1px solid #ffe4b5' }}>Updated</span>}
                                        {normalized?.mode === 'created' && <span className="ms-auto badge" style={{ background: '#e8f8f0', color: '#1a9e5c', border: '1px solid #b9e8d0' }}>Created</span>}
                                        {normalized?.mode === 'deleted' && <span className="ms-auto badge" style={{ background: '#fff0f0', color: '#e03131', border: '1px solid #ffc9c9' }}>Deleted</span>}
                                    </div>
                                    <div className="card-body p-0">
                                        {renderDetailsSection()}
                                    </div>
                                </div>

                                {log.ipAddress && (
                                    <div className="mt-3 text-muted" style={{ fontSize: 12 }}>
                                        <i className="bi bi-geo-alt me-1"></i>IP: {log.ipAddress} {log.userAgent && `• ${log.userAgent}`}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </ContainerDashboard>
        </>
    )
}

export default AuditLogDetails
